import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Nav";
import { api, errMsg } from "../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import ScannerControls from "./ScannerControls";
import { ArrowLeft, RefreshCw, Users, DollarSign, Wallet, Repeat, Search } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const PLAN_LABEL = { trial: "3-Day Trial", monthly: "Monthly", lifetime: "Lifetime" };

const AdminPanel = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data: d } = await api.get("/admin/overview"); setData(d); }
    catch (e) { toast.error(errMsg(e, "Could not load admin data.")); }
    setBusy(false);
  }, []);

  useEffect(() => { if (user?.role === "owner") load(); }, [user, load]);

  const members = useMemo(() => (data?.members || []).filter((m) => {
    const s = q.toLowerCase();
    const okQ = !s || m.email.includes(s) || m.name.toLowerCase().includes(s);
    const okF = filter === "all" || (filter === "active" ? m.status === "active" : filter === "inactive" ? m.status !== "active" : m.plan === filter && m.status === "active");
    return okQ && okF;
  }), [data, q, filter]);

  const act = async (m, action) => {
    try {
      await api.post(`/admin/members/${m.id}/access`, { action });
      toast.success(action === "revoke" ? `Access revoked for ${m.email}` : `30 days of Monthly granted to ${m.email}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Action failed.")); }
  };

  if (loading) return <div id="memberDash" />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;

  const st = data?.stats, sp = data?.stripe;
  const cards = [
    { icon: Users, lbl: "Members", val: st ? st.members : "—", sub: st ? `${st.active} active` : "" },
    { icon: DollarSign, lbl: "Revenue (All Time)", val: st ? money(st.revenue_total) : "—", sub: st ? `${money(st.revenue_30d)} last 30 days` : "" },
    { icon: Repeat, lbl: "Monthly Recurring", val: st ? money(st.mrr) : "—", sub: st ? `${st.counts.monthly} monthly subscribers` : "" },
    { icon: Wallet, lbl: "Stripe Balance", val: sp?.ok ? money(sp.available) : "—", sub: sp?.ok ? `${money(sp.pending)} pending · ${sp.mode} mode` : "Stripe unavailable" },
  ];

  return (
    <div className="admin" data-testid="admin-panel">
      <div className="dash-nav">
        <Logo />
        <div className="dash-nav-r">
          <span className="dash-name">Owner Panel</span>
          <button className="nav-btn nb-login" onClick={() => nav("/dashboard")} data-testid="admin-back"><ArrowLeft size={12} /> Dashboard</button>
        </div>
      </div>
      <div className="admin-wrap">
        <div className="admin-head">
          <div>
            <div className="section-eye left">Private · Owner Only</div>
            <h2 className="section-h left">Your Business,<br /><em>At A Glance.</em></h2>
          </div>
          <div className="bot-head-r">
          <button className="mini-btn" onClick={() => nav("/scanner")} data-testid="open-scanner">AI Scanner →</button>
          <button className="news-refresh" onClick={load} aria-label="Refresh" data-testid="admin-refresh"><RefreshCw size={13} className={busy ? "spin" : ""} /></button>
          </div>
        </div>

        <ScannerControls />

        <div className="admin-cards">
          {cards.map((c) => (
            <div className="live-card admin-card" key={c.lbl} data-testid={`admin-card-${c.lbl.split(" ")[0].toLowerCase()}`}>
              <c.icon size={16} className="ac-ico" />
              <div className="lc-eye">{c.lbl}</div>
              <div className="ac-val">{c.val}</div>
              <div className="ac-sub">{c.sub}</div>
            </div>
          ))}
        </div>

        {st && (
          <div className="plan-split">
            {["trial", "monthly", "lifetime", "expired", "none"].map((k) => (
              <div key={k}><b>{st.counts[k] || 0}</b><span>{k === "none" ? "No plan" : k === "expired" ? "Expired" : PLAN_LABEL[k]}</span></div>
            ))}
            {sp?.ok && <div><b>{money(sp.net)}</b><span>Stripe net ({sp.charges} charges)</span></div>}
          </div>
        )}

        <div className="live-card">
          <div className="admin-tbar">
            <div className="lc-eye" style={{ margin: 0 }}>Members ({members.length})</div>
            <div className="admin-filters">
              <div className="admin-search"><Search size={13} /><input placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" /></div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="live-select admin-sel" data-testid="admin-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="live-select-pop">
                  <SelectItem value="all">All members</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="lifetime">Lifetime</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="admin-table">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Member</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead>Expires</TableHead><TableHead>Joined</TableHead><TableHead>Paid</TableHead><TableHead className="tr">Access</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {members.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="empty-cell">{data ? "No members match." : "Loading…"}</TableCell></TableRow>
                ) : members.map((m) => (
                  <TableRow key={m.id} data-testid="admin-member-row">
                    <TableCell><div className="m-name">{m.name}</div><div className="m-email">{m.email}</div></TableCell>
                    <TableCell>{m.plan ? PLAN_LABEL[m.plan] : "—"}</TableCell>
                    <TableCell><span className={`pill ${m.status}`}>{m.status === "none" ? "no plan" : m.status}</span></TableCell>
                    <TableCell>{m.plan === "lifetime" && m.status === "active" ? "Never" : date(m.plan_expires)}</TableCell>
                    <TableCell>{date(m.created_at)}</TableCell>
                    <TableCell>{money(m.total_paid)}</TableCell>
                    <TableCell className="tr">
                      {m.status === "active"
                        ? <button className="mini-btn danger" onClick={() => act(m, "revoke")} data-testid="admin-revoke">Revoke</button>
                        : <button className="mini-btn" onClick={() => act(m, "grant_monthly")} data-testid="admin-grant">Grant 30 Days</button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="live-card">
          <div className="lc-eye">Recent Payments</div>
          {(data?.recent_payments || []).length === 0 ? <div className="sig-wait">No payments yet.</div> : data.recent_payments.map((p) => (
            <div className="hist pay-row" key={p.session_id}><span>{date(p.created_at)}</span><span>{p.email}</span><b className="up">{PLAN_LABEL[p.plan_id] || p.plan_id}</b><span>{money(p.amount)}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
